import { Dispatch, SetStateAction } from 'react';
import { useParams } from 'react-router-dom';

import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';

import { GroupNewApiInputs } from '@type/group';

import { useInternalRouter } from '@hooks/useInternalRouter';
import useQueryString from '@hooks/useQueryString';

// import { auth } from '@constants/state';

import {
  CREATE_ERROR_MESSAGE,
  CREATE_SUCCESS_MESSAGE,
  DELETE_ERROR_MESSAGE,
  DELETE_SUCCESS_MESSAGE,
  EDIT_ERROR_MESSAGE,
  EDIT_SUCCESS_MESSAGE,
} from '@constants/toastMessage';

import { create, getList, getOne, remove, update } from '@apis/api';

/** 조합 관리 - 목록 조회 */
export function useGroupList(customPageSize?: number) {
  const { pageNo, pageSize } = useQueryString({
    pageSize: customPageSize || 10,
  });

  const { data, isLoading } = useQuery(
    ['fund-list', pageNo, pageSize],
    () => getList(`/company/self/fund?page=${pageNo}&size=${pageSize}`, ''),
    {
      suspense: true,
    }
  );

  return {
    data: data?.content,
    totalCount: data?.totalCount,
    isLoading,
  };
}

/** 조합 관리 - 상세 */
export function useGroupDetail() {
  const { groupId } = useParams();

  const { data, isLoading } = useQuery(
    ['group-detail', groupId],
    () => getOne('/company/self/fund', groupId),
    {
      suspense: true,
    }
  );

  return {
    data,
    isLoading,
    investment: data?.investment,
  };
}

/** 조힙 관리 > 상세 > 조합 실적 */
export function useGroupValuation() {
  const { groupId } = useParams();

  const { data, isLoading } = useQuery(
    ['group-valuation', groupId],
    () => getList(`/fund/${groupId}/valuation`, ''),
    {
      suspense: true,
    }
  );

  return {
    data: data?.content || [],
    totalCount: data?.totalCount || 0,
    isLoading,
  };
}

/** 조합 관리 > 전체 포트폴리오사 목록 */
export function useTotalPortfolioList(pageNo: number) {
  const { pageSize, keyword } = useQueryString();
  const { data, isLoading } = useQuery(
    ['total-portfolio-list', pageNo, pageSize, keyword],
    () =>
      getList(`/company/self/fund/portfolio`, {
        page: pageNo,
        size: pageSize,
        portfolioName: keyword,
      }),
    {
      suspense: true,
    }
  );

  return {
    data: data?.content || [],
    totalCount: data?.totalCount || 0,
    isLoading,
  };
}

/** 조합 관리 > 전체 포트폴리오사 목록 간단 조회 */
export function useTotalPortfolioSimpleList() {
  const { data, isLoading } = useQuery(
    ['total-portfolio-simple-list'],
    () => getList(`/company/self/fund/portfolio?simple=`, ''),
    {
      suspense: true,
    }
  );

  return {
    simpleListData: data?.map((d: { id: number; name: string }) => ({
      value: d.id,
      label: d.name,
    })),
    isSimpleListLoading: isLoading,
  };
}

/** 조합 관리 > 조합 등록 */
export function useGroupCreate(
  setConfirmModalOpen: Dispatch<SetStateAction<boolean>>
) {
  const router = useInternalRouter();
  const { mutate, isLoading } = useMutation(
    ['group-new'],
    (value: GroupNewApiInputs) => create('/company/self/fund', value),
    {
      onSuccess: (res: any) => {
        if (res?.isError) {
          toast.error(CREATE_ERROR_MESSAGE);
          console.log(`ERROR[${res?.data?.errorCode}] : ${res?.data?.message}`);
          setConfirmModalOpen(false);
        } else {
          toast.success(CREATE_SUCCESS_MESSAGE);
          router.replace('/group');
          setConfirmModalOpen(false);
        }
      },
    }
  );

  return {
    mutate,
    isLoading,
  };
}

/** 조합 관리 > 조합 수정 */
export function useGroupUpdate(
  setConfirmModalOpen: Dispatch<SetStateAction<boolean>>
) {
  const router = useInternalRouter();
  const queryClient = useQueryClient();
  const { groupId } = useParams();
  const { mutate, isLoading } = useMutation(
    ['group-update', groupId],
    (value: GroupNewApiInputs) =>
      update(`/company/self/fund/${groupId}`, value),
    {
      onSuccess: (res: any) => {
        if (res?.isError) {
          toast.error(EDIT_ERROR_MESSAGE);
          console.log(`ERROR[${res?.data?.errorCode}] : ${res?.data?.message}`);
          setConfirmModalOpen(false);
        } else {
          toast.success(EDIT_SUCCESS_MESSAGE);
          queryClient.removeQueries(['group-detail', groupId]);
          router.replace(`/group/${groupId}`);
          setConfirmModalOpen(false);
        }
      },
    }
  );

  return {
    mutate,
    isLoading,
  };
}

/** 조합 관리 > 조합 삭제 */
export function useGroupDelete(
  setConfirmModalOpen: Dispatch<SetStateAction<boolean>>
) {
  const router = useInternalRouter();
  const { groupId } = useParams();
  const { mutate, isLoading } = useMutation(
    ['group-delete', groupId],
    () => remove(`/company/self/fund/${groupId}`),
    {
      onSuccess: (res: any) => {
        if (res?.isError) {
          toast.error(DELETE_ERROR_MESSAGE);
          console.log(`ERROR[${res?.data?.errorCode}] : ${res?.data?.message}`);
          setConfirmModalOpen(false);
        } else {
          toast.success(DELETE_SUCCESS_MESSAGE);
          router.replace(`/group`);
          setConfirmModalOpen(false);
        }
      },
    }
  );

  return {
    mutate,
    isLoading,
  };
}
